import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Box,
  IconButton,
  Tooltip,
  Checkbox,
  Chip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  styled,
  alpha,
} from "@mui/material";
import {
  FileDownload as DownloadIcon,
  FilterList as FilterIcon,
  ViewColumn as ViewColumnIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useState, useMemo } from "react";
import * as XLSX from "xlsx";

export interface EnhancedColumnProps {
  dataIndex: string;
  key: string;
  title: string;
  render?: (cellData: any, row: any) => any;
  sortable?: boolean;
  filterable?: boolean;
  width?: string | number;
  align?: "left" | "center" | "right";
  hidden?: boolean;
}

interface EnhancedTableProps {
  columns: EnhancedColumnProps[];
  dataSource: any[];
  loading?: boolean;
  rowKey?: string;
  onRowClick?: (row: any) => void;
  selectable?: boolean;
  onSelectionChange?: (selectedRows: any[]) => void;
  exportable?: boolean;
  exportFileName?: string;
  searchable?: boolean;
  pagination?: {
    page: number;
    limit: number;
    totalCount: number;
    onChange: (page: number) => void;
    onChangeLimit?: (limit: number) => void;
  };
}

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "clickable",
})<{ clickable?: boolean }>(({ theme, clickable }) => ({
  cursor: clickable ? "pointer" : "default",
  transition: "all 0.15s ease",
  borderBottom: "1px solid rgba(226, 232, 240, 0.6)",
  "&:hover": {
    backgroundColor: clickable ? alpha(theme.palette.primary.main, 0.03) : "rgba(248, 250, 252, 0.5)",
  },
  "&.Mui-selected": {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
  "&:last-child": {
    borderBottom: "none",
  },
}));

const TableToolbar = styled(Box)(({ theme }) => ({
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  borderBottom: "1px solid rgba(226, 232, 240, 0.6)",
  backgroundColor: "#FFFFFF",
}));

export const EnhancedTable = ({
  columns,
  dataSource,
  loading = false,
  rowKey = "id",
  onRowClick,
  selectable = false,
  onSelectionChange,
  exportable = false,
  exportFileName = "export",
  searchable = false,
  pagination,
}: EnhancedTableProps) => {
  const [selected, setSelected] = useState<any[]>([]);
  const [orderBy, setOrderBy] = useState<string>("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  // Filtered and sorted data
  const processedData = useMemo(() => {
    let filtered = dataSource;

    // Search filter
    if (searchQuery && searchable) {
      filtered = filtered.filter((row) =>
        columns.some((col) => {
          const value = row[col.dataIndex];
          return value
            ?.toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        })
      );
    }

    // Sorting
    if (orderBy) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (aValue === bValue) return 0;
        if (order === "asc") {
          return aValue > bValue ? 1 : -1;
        }
        return aValue < bValue ? 1 : -1;
      });
    }

    return filtered;
  }, [dataSource, searchQuery, orderBy, order, columns, searchable]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(processedData);
      onSelectionChange?.(processedData);
    } else {
      setSelected([]);
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (row: any) => {
    const selectedIndex = selected.findIndex(
      (item) => item[rowKey] === row[rowKey]
    );
    let newSelected: any[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, row];
    } else {
      newSelected = selected.filter((item) => item[rowKey] !== row[rowKey]);
    }

    setSelected(newSelected);
    onSelectionChange?.(newSelected);
  };

  const isSelected = (row: any) =>
    selected.findIndex((item) => item[rowKey] === row[rowKey]) !== -1;

  const handleExport = (format: "csv" | "excel") => {
    const exportData = (selected.length > 0 ? selected : processedData).map(
      (row) =>
        columns
          .filter((col) => !hiddenColumns.includes(col.key))
          .reduce((acc, col) => {
            acc[col.title] = row[col.dataIndex];
            return acc;
          }, {} as any)
    );

    if (format === "csv") {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exportFileName}.csv`;
      a.click();
    } else {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      XLSX.writeFile(wb, `${exportFileName}.xlsx`);
    }
    setExportMenuAnchor(null);
  };

  const visibleColumns = columns.filter(
    (col) => !hiddenColumns.includes(col.key)
  );

  return (
    <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid rgba(226, 232, 240, 0.8)", overflow: "hidden" }}>
      {/* Toolbar */}
      {(searchable || exportable || selectable) && (
        <TableToolbar>
          {/* Search */}
          {searchable && (
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
          )}

          <Box flex={1} />

          {/* Selection Info */}
          {selectable && selected.length > 0 && (
            <Chip
              label={`${selected.length} selected`}
              onDelete={() => {
                setSelected([]);
                onSelectionChange?.([]);
              }}
              color="primary"
            />
          )}

          {/* Column Visibility */}
          <Tooltip title="Column Visibility">
            <IconButton
              size="small"
              onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
            >
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={columnMenuAnchor}
            open={Boolean(columnMenuAnchor)}
            onClose={() => setColumnMenuAnchor(null)}
          >
            {columns.map((col) => (
              <MenuItem
                key={col.key}
                onClick={() => {
                  setHiddenColumns((prev) =>
                    prev.includes(col.key)
                      ? prev.filter((k) => k !== col.key)
                      : [...prev, col.key]
                  );
                }}
              >
                <Checkbox checked={!hiddenColumns.includes(col.key)} />
                <ListItemText primary={col.title} />
              </MenuItem>
            ))}
          </Menu>

          {/* Export */}
          {exportable && (
            <>
              <Tooltip title="Export">
                <IconButton
                  size="small"
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
              >
                <MenuItem onClick={() => handleExport("csv")}>
                  <ListItemText primary="Export as CSV" />
                </MenuItem>
                <MenuItem onClick={() => handleExport("excel")}>
                  <ListItemText primary="Export as Excel" />
                </MenuItem>
              </Menu>
            </>
          )}
        </TableToolbar>
      )}

      {/* Table */}
      <TableContainer>
        <MuiTable>
          <TableHead sx={{ backgroundColor: "#F8FAFC", "& th": { borderBottom: "1px solid rgba(226, 232, 240, 0.8)", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748B", py: 1.5, px: 2 } }}>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < processedData.length
                    }
                    checked={
                      processedData.length > 0 &&
                      selected.length === processedData.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{ fontWeight: 600 }}
                >
                  {col.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === col.dataIndex}
                      direction={orderBy === col.dataIndex ? order : "asc"}
                      onClick={() => handleRequestSort(col.dataIndex)}
                    >
                      {col.title}
                    </TableSortLabel>
                  ) : (
                    col.title
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                  align="center"
                >
                  <Typography>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : processedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                  align="center"
                >
                  <Typography color="text.secondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              processedData.map((row) => {
                const rowIsSelected = isSelected(row);
                return (
                  <StyledTableRow
                    key={row[rowKey]}
                    clickable={!!onRowClick}
                    onClick={() => !selectable && onRowClick?.(row)}
                    selected={rowIsSelected}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={rowIsSelected}
                          onChange={() => handleSelectRow(row)}
                        />
                      </TableCell>
                    )}
                    {visibleColumns.map((col) => (
                      <TableCell key={col.key} align={col.align || "left"}>
                        {col.render
                          ? col.render(row[col.dataIndex], row)
                          : row[col.dataIndex]}
                      </TableCell>
                    ))}
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {/* Pagination */}
      {pagination && (
        <TablePagination
          component="div"
          count={pagination.totalCount}
          page={pagination.page}
          onPageChange={(_, newPage) => pagination.onChange(newPage)}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={(e) =>
            pagination.onChangeLimit?.(parseInt(e.target.value, 10))
          }
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      )}
    </Paper>
  );
};

export default EnhancedTable;

export { };
